import {
  auth,
  firestore,
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  updateDoc,
  getFunctions,
  httpsCallable,
  signInAnonymously,
} from '../../utils/firestore'
import * as constants from './constants' 
import { today } from 'irmlib/common'
import * as gdeTransactions from './gdeTransactions'

let recordDataObject = {}
const logoutTime = 'loggedOutTs'
const totalTime = 'loggedTime'
const workerLoginFunctionName = 'grpGde-WorkerLoginService'
const workerLookupResponseKeys = ['pinActive', 'pinExists', 'workerId']

const validateWorkerLookupResponse = (data, workerId) => {
  const responseKeys = data && typeof data === 'object' ? Object.keys(data).sort() : []
  const hasExpectedKeys = responseKeys.length === workerLookupResponseKeys.length &&
    responseKeys.every((key, index) => key === workerLookupResponseKeys[index])
  if (
    !hasExpectedKeys ||
    data.workerId !== workerId ||
    typeof data.pinExists !== 'boolean' ||
    typeof data.pinActive !== 'boolean'
  ) {
    throw new Error('Invalid worker login lookup response')
  }
  return data
}

const containsPinData = (value) => {
  if (!value || typeof value !== 'object') return false
  return Object.entries(value).some(([key, nestedValue]) =>
    ['pin', 'gdepin'].includes(key.toLowerCase()) || containsPinData(nestedValue))
}

const validateWorkerProfileResponse = (data, workerId) => {
  if (
    !data ||
    typeof data.worker !== 'object' ||
    typeof data.gdeRole !== 'object' ||
    data.worker.workerId !== workerId
  ) {
    throw new Error('Invalid worker login profile response')
  }
  if (containsPinData(data)) {
    throw new Error('Worker login response must not contain PIN data')
  }
  return data
}

export const recordLogin = async (user) => {
  const gdeWorkerDetails = await gdeTransactions.getGdeWorkerDetails(user.workerId)
  const rawDataGde = gdeWorkerDetails.data()
  
  let userCountry = user.country
  if (rawDataGde?.gdeCountry !== undefined) {
    userCountry = rawDataGde.gdeCountry
  }

  recordDataObject = { 
    workerId: user.workerId,
    loggedDate: today(),
    loggedInTs: new Date(),
    activityType: constants.loginType,
    workerCountry: userCountry,
    workerFirstName: user.firstName,
    workerLastName: user.lastName,
    districtId: user.district ? user.district : '',
    metricsId: Date.now(),
    isDeleted: false
  }
  await gdeTransactions.saveMetricsData(recordDataObject)
}

export const updateLogin = async (user) => {
  const metricsId = recordDataObject.metricsId
  const workerId = recordDataObject.workerId
  const workerMetricsData = await gdeTransactions.getLoginMetrics(workerId, metricsId)
  let metricsCollectionId = ''
  workerMetricsData.forEach((doc) => {
    metricsCollectionId = doc.id
  })
  const loggingTime = recordDataObject.loggedInTs?.getTime()
  recordDataObject[logoutTime] = new Date()
  recordDataObject[totalTime] = Math.round((new Date().getTime() - loggingTime) / 1000)
  const metricsData = {
    loggedTime: recordDataObject[totalTime],
    loggedOutTs: recordDataObject[logoutTime],
    metricsCollectionId: metricsCollectionId
  }
  metricsData.metricsCollectionId && await gdeTransactions.updateMetricsForLogin(metricsData)
}

export const beginWorkerPinLogin = async (workerId) => {
  await signInAnonymously(auth)
  try {
    const workerLogin = httpsCallable(getFunctions(), workerLoginFunctionName)
    const result = await workerLogin({ action: 'lookup', workerId })
    return validateWorkerLookupResponse(result.data, workerId)
  } catch (error) {
    await auth.signOut()
    throw error
  }
}

export const submitWorkerPin = async (action, workerId, pin) => {
  try {
    const workerLogin = httpsCallable(getFunctions(), workerLoginFunctionName)
    const result = await workerLogin({ action, workerId, pin })
    return validateWorkerProfileResponse(result.data, workerId)
  } catch (error) {
    // Keep the anonymous session so the enter-PIN dialog can retry a wrong PIN.
    const message = `${error?.message || ''}`.toLowerCase()
    const isIncorrectPin = error?.code === 'functions/permission-denied' &&
      message.includes('incorrect pin')
    if (!isIncorrectPin) {
      await auth.signOut()
    }
    throw error
  }
}

export const cancelWorkerPinLogin = async () => {
  await auth.signOut()
}


const getWorkerInfo = async emailId => getDocs(query(collection(firestore, constants.workerCollectionName), where('email', '==', emailId), where('isActiveWorker', '==', true), where('isDeleted', '==', false)))

const gdeWorkerRole = async workerId => getDoc(doc(firestore, constants.workerCollectionName, workerId, constants.workerRolesCollectionName, constants.gdeWorkerRoleName))

const updateWorkerRoleEmail = async (workerId, email) => updateDoc(doc(firestore, constants.workerCollectionName, workerId), 'email', email)
const updateWorkerRoleDistrict = async (workerId, district) => updateDoc(doc(firestore, constants.workerCollectionName, workerId), 'gdeDistrict', district)


export { getWorkerInfo, gdeWorkerRole, updateWorkerRoleEmail, updateWorkerRoleDistrict }

